import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LocaSmartLayout } from '../locasmart-layout';
import { TabControl } from '../tab-control';
import InformacoesTab from './informacoesTab';
import SegurancaTab from './segurancaTab';
import {
    ehPlanoGratuito,
    estaEmPeriodoDeTeste,
    expiraEm,
    obterEmailUsuario,
    obterIdTenent,
    obterNomeEmpresa,
    obterTipoPlano
} from '../../services/seguranca';
import { useAppSelector } from '../../store/hooks';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { ConfiguracaoEmpresa, Empresa } from '../../services/tipos';
import AssinaturaTab from './assinaturaTab';
import { calcularTempoRestante, parseDataBRParaDate } from '../../services/utilitarios';
import { ConfiguracaoTab } from './ConfiguracaoTab';
import { IntegracaoTab } from './integracaoTab';
import { NfseTab } from './NfseTab';
import { HiOutlinePuzzlePiece } from "react-icons/hi2";
import { HiOutlineDocumentText } from "react-icons/hi2";

export const MinhaConta: React.FC = () => {
    const [token] = useState(useAppSelector(state => state.token));
    const [empresa, setEmpresa] = useState<Empresa | null>(null);
    const [configuracaoEmpresa, setConfiguracaoEmpresa] = useState<ConfiguracaoEmpresa>({} as ConfiguracaoEmpresa);
    const [loading, setLoading] = useState(true);
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const abaInicial = queryParams.get('aba');

    const tratarErro = (error: any) => {
        const errorMessage = error.response?.data?.Mensagem || 'Erro desconhecido';
        toast.error('Não foi possível continuar a operação: ' + errorMessage);
    };

    const [tipoPlanoAtual, setTipoPlanoAtual] = useState<'PRO' | 'AVANCADO' | null>(null);

    useEffect(() => {
        const carregarPlano = async () => {
            if (!token) return;

            try {
                const plano = await obterTipoPlano(token);
                setTipoPlanoAtual(plano?.toUpperCase() as 'PRO' | 'AVANCADO');
            } catch {
                setTipoPlanoAtual(null);
            }
        };

        carregarPlano();
    }, [token]);

    useEffect(() => {
        const fetchDados = async () => {
            setLoading(true);
            try {
                const [empresaResp, configuracaoResp] = await Promise.all([
                    api.get(`Empresa/${obterIdTenent(token)}`),
                    api.get(`ConfiguracaoPorEmpresa/${obterIdTenent(token)}`)
                ]);
                setEmpresa(empresaResp.data);
                setConfiguracaoEmpresa(configuracaoResp.data);
            } catch (error: any) {
                tratarErro(error);
            } finally {
                setLoading(false);
            }
        };
        fetchDados();
    }, [token]);

    if (loading) {
        return (
            <LocaSmartLayout>
                <div className="flex justify-center items-center h-64">
                    <span>Carregando...</span>
                </div>
            </LocaSmartLayout>
        );
    }

    if (!empresa) {
        return (
            <LocaSmartLayout>
                <div className="flex justify-center items-center h-64">
                    <span>Não foi possível carregar os dados da empresa</span>
                </div>
            </LocaSmartLayout>
        );
    }

    const tabs = [
        {
            id: 'informacoes',
            friendlyName: 'Informações',
            label: (
                <span className="flex items-center whitespace-nowrap">
                    <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Informações
                </span>
            ),
            content: (
                <InformacoesTab
                    initialData={{
                        name: obterNomeEmpresa(token).toUpperCase(),
                        document: empresa.documento,
                        address: {
                            street: empresa.rua || '',
                            city: empresa.cidade || '',
                            state: empresa.estado || '',
                            zipCode: empresa.cep || '',
                            complement: empresa.complemento || '',
                            number: empresa.numero || '',
                            bairro: empresa.bairro || '',
                        },
                        email: obterEmailUsuario(token),
                        logoUrl: empresa.logo || '',
                    }}
                />
            ),
        },
        {
            id: 'seguranca',
            friendlyName: 'Segurança',
            label: (
                <span className="flex items-center whitespace-nowrap">
                    <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                            d="M12 11c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.7-3 3v1m0 4v-1m-9-6h18M5 11V7a5 5 0 0110 0v4" />
                    </svg>
                    Segurança
                </span>
            ),
            content: <SegurancaTab email={obterEmailUsuario(token)} />,
        },
        {
            id: 'assinatura',
            friendlyName: 'Assinatura',
            label: (
                <span className="flex items-center whitespace-nowrap">
                    <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                            d="M3 10h18M4 6h16a1 1 0 011 1v10a1 1 0 01-1 1H4a1 1 0 01-1-1V7a1 1 0 011-1z" />
                    </svg>
                    Assinatura
                </span>
            ),
            content: (
                <AssinaturaTab
                    assinaturaData={{
                        limiteVeiculos: empresa.limiteDeVeiculos,
                        proximaRenovacao: ehPlanoGratuito(token) ? 'Não consta' : expiraEm(token),
                        tempoRestante: ehPlanoGratuito(token)
                            ? 'Não consta'
                            : calcularTempoRestante(parseDataBRParaDate(expiraEm(token))),
                        planoAtual: estaEmPeriodoDeTeste(token)
                            ? 'EXPERIMENTAL'
                            : ehPlanoGratuito(token)
                                ? 'GRATUITO'
                                : tipoPlanoAtual
                                    ? tipoPlanoAtual.replace('AVANCADO', 'AVANÇADO')
                                    : 'PRO',

                    }}
                />
            ),
        },
        {
            id: 'configuracao',
            friendlyName: 'Configuração',
            label: (
                <span className="flex items-center whitespace-nowrap">
                    <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                            d="M12 15.5A3.5 3.5 0 1112 8.5a3.5 3.5 0 010 7z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                            d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51h.09a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v.09a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
                    </svg>
                    Configuração
                </span>
            ),
            content: (
                <ConfiguracaoTab
                    empresaId={obterIdTenent(token)}
                    initialMessage={configuracaoEmpresa?.mensagemWhatsAppCobranca || ''}
                    initialChavePix={configuracaoEmpresa?.chavePix || ''}
                    initialMessageManutencao={configuracaoEmpresa?.mensagemWhatsAppManutencao || ''}
                    rastreadorAtivo={!!configuracaoEmpresa?.azulMonitorConfigurado}
                    initialComissaoApurarSomenteSePago={configuracaoEmpresa?.comissaoApurarSomenteSePago ?? true}
                />
            ),
        },
        {
            id: 'integracao',
            friendlyName: 'Integrações',
            label: (
                <span className="flex items-center whitespace-nowrap">
                    <HiOutlinePuzzlePiece className="w-5 h-5 mr-1" />
                    Integrações
                </span>
            ),
            content: <IntegracaoTab empresaId={obterIdTenent(token)} initialChaveApiAsaas={configuracaoEmpresa?.chaveAPIAsaas} initialChaveApiAutentique={configuracaoEmpresa?.autentiqueApiKey} initialChaveApiAsaasConfigurada={configuracaoEmpresa?.chaveAPIAsaasConfigurada} initialChaveApiAsaasFinal={configuracaoEmpresa?.chaveAPIAsaasFinal} initialChaveApiAutentiqueConfigurada={configuracaoEmpresa?.autentiqueApiKeyConfigurada} initialChaveApiAutentiqueFinal={configuracaoEmpresa?.autentiqueApiKeyFinal} initialAutentiqueRemetenteEmail={configuracaoEmpresa?.autentiqueRemetenteEmail} initialAzulMonitorEmail={configuracaoEmpresa?.azulMonitorEmail} initialAzulMonitorConfigurado={configuracaoEmpresa?.azulMonitorConfigurado} />,
        },
        {
            id: 'nfse',
            friendlyName: 'Notas Fiscais',
            label: (
                <span className="flex items-center whitespace-nowrap">
                    <HiOutlineDocumentText className="w-5 h-5 mr-1" />
                    Notas Fiscais
                </span>
            ),
            content: <NfseTab empresaId={obterIdTenent(token)} initialData={configuracaoEmpresa} emissaoNotaFiscalLiberada={empresa?.emissaoNotaFiscalLiberada} empresaDocumento={empresa?.documento} empresaNome={empresa?.nome} onEmpresaAtualizada={() => { api.get(`Empresa/${obterIdTenent(token)}`).then(r => setEmpresa(r.data)); }} />,
        }
    ];

    const abaPadrao: string | undefined = tabs.some(tab => tab.id === abaInicial) ? abaInicial ?? undefined : undefined;

    return (
        <LocaSmartLayout>
            <div className="overflow-x-auto">
                <TabControl tabs={tabs} defaultActiveTabId={abaPadrao} />
            </div>
        </LocaSmartLayout>
    );
};
